import React, { useCallback } from 'react';
import { useFocusEffect, useRouter } from 'expo-router';
import { defineMessages, type FormatNumberOptions, useIntl } from 'react-intl';
import { Platform, StyleSheet, Text, View } from 'react-native';

import {
  BodyText,
  FocusScope,
  Modal,
  TextSpace,
  theme,
  useAppState,
  useKeyboardListener,
  useIsFocused,
  useNoticeBannersStackContext,
} from '@sg/garnish';
import { OrderState, type Product } from '@sg/graphql-schema';
import { BagRewardWarning } from '@sg/shared-fe-features-loyalty';

import {
  GroupOrderBagButton,
  GroupOrderBagModal,
  GroupOrderConfirmationContent,
  useGroupOrderingContent,
} from '@order/features-group-ordering';
import { adjustWantedTime, Bag } from '@order/features-ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { isTomorrow } from '@order/utils';

import { GroupOrderShareContent } from '../../../../components';
import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useNavigateToLastInteractedStore } from '../../../../context/LastInteractedStore';
import {
  useBagContent,
  useIsUpsellScreenEnabled,
  useNavigateToMenu,
  useUpsellPlacement,
} from '../../../../hooks';
import { useBagDisclosure, type useOrderState, useUpsells } from '../../hooks';
import { BagLineItems } from '../BagLineItems';
import { BagLoyalty } from '../BagLoyalty';
import { CategoryUpsells } from '../CategoryUpsells';
import { GroupOrderConvertAction } from '../GroupOrderConvertAction';
import { GroupOrderConvertModal } from '../GroupOrderConvertModal';

export const BagContent = (props: BagContentProps) => {
  const { orderState } = props;

  const { formatMessage, formatNumber } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const isLoggedIn = useIsLoggedIn();
  const { mappedContent: groupOrderingContent } = useGroupOrderingContent();

  // ─── Flags ────────────────────────────────────────────────────────────────

  const {
    isBagEmpty,
    isBagValid,
    isFetchingInitialData,
    isSubmittingOrder,
    isShowingFutureDateWarning,
    isShowingRewardWarning,
    isShowingTimeslotAdjustedNotice,
    isLineItemQuantityChangePending,
    isShowingGroupOrderConvertModal,
    isConvertingGroupOrder,
    isShowingGroupOrderShareModal,
    isShowingGroupOrderLeaveModal,
    isLeavingGroupOrder,
    isShowingGroupOrderLockModal,
    isLockingGroupOrder,
    isUnlockingGroupOrder,
    isPerformingBagAction,
    isShowingWarning,
    isShowingModal,
    isPostOrder,
    isPollingPayment,
    isPaymentPending,
    isPaymentDelayed,
    isPaymentFulfilled,
    isShowingOrderPlayback,
    isCancelingPayment,
  } = orderState;

  const { placementVariation, isLoadingPlacement } = useUpsellPlacement();

  const isFocused = useIsFocused();
  const isUpsellScreenEnabled = useIsUpsellScreenEnabled();
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  const isFocusable = !isShowingModal && !isShowingWarning;

  const canContinueToCheckout =
    !isPerformingBagAction && !isLineItemQuantityChangePending && !isShowingModal;

  // ─── Context ──────────────────────────────────────────────────────────────

  const {
    bagTracking,
    wantedTime,
    previousWantedTime,
    dropoffLocation = '',
    lineItemIdsBeingUpdated,
    cart,
    rewardError,
    isRewardMissingSku,
    shouldIncludeUtensils,
    upsellItems,
  } = orderState;

  // ─── Cart ─────────────────────────────────────────────────────────────────

  const {
    orderId = '',
    orderChannel = 'pickup',
    locationName = '',
    restaurantId = '',
    restaurantSlug = '',
    dropoffLocations,
    addressId,
    isGroupOrder,
    lineItems,
    ledger,
    showDeliveryFeeDisclosure = false,
    showOutpostPriceDifferenciationDisclosure = false,
    showDeliveryPriceDifferenciationDisclosure = false,
    deliveryFee = 0,
    deliveryMinSubtotal = 0,
    groupOrderJoinCode,
    canApplyRewards,
    canSubmitOrder,
    canUpdateOrderDetails,
  } = cart;

  // ─── Actions ──────────────────────────────────────────────────────────────

  const {
    startPostRedirectDelay,
    addUpsell,
    isAddingUpsellToBag,
    startChangingLineItemQuantity,
    stopChangingLineItemQuantity,
    changeLineItemQuantity,
    closeBag,
    cancelRequest,
    cancelPreviousIntent,
    requestChannelOrLocationChange,
    requestDeliveryPreferencesChange,
    requestTimeChange,
    changeDropoffLocation,
    changeUtensilsUsage,
    requestCheckoutLedger,
    requestGroupOrderConversion,
    convertToGroupOrder,
    requestGroupOrderShare,
    requestGroupOrderLeave,
    leaveGroupOrder,
    lockGroupOrder,
    unlockGroupOrder,
  } = orderState;

  // ─── Headers ──────────────────────────────────────────────────────────────

  const adjustedTime = adjustWantedTime(wantedTime);
  const isFutureDate = adjustedTime ? isTomorrow(adjustedTime) : false;
  const hasLongerLocationName = locationName.length > 24;

  // ─── Group Order ──────────────────────────────────────────────────────────

  const shouldShowGroupOrderConvertAction = isGroupOrderingEnabled && !isBagEmpty && !isGroupOrder;
  const shouldShowGroupOrder = isGroupOrderingEnabled && isGroupOrder;
  const canSubmitCartOrder = !shouldShowGroupOrder || canSubmitOrder;
  const shouldShowGroupOrderHostActions = shouldShowGroupOrder && canSubmitCartOrder;

  const isCartLocked =
    cart.orderState === OrderState.LockedCheckout ||
    cart.orderState === OrderState.LockedChangeLocation;
  const isGroupOrderLocked = Boolean(isGroupOrder && isCartLocked);
  const isGroupOrderCheckoutLocked =
    shouldShowGroupOrderHostActions && cart.orderState === OrderState.LockedCheckout;
  const canApplyCartRewards = (!shouldShowGroupOrder || canApplyRewards) && !isGroupOrderLocked;
  const canUpdateCartOrderDetails =
    (!shouldShowGroupOrder || canUpdateOrderDetails) && !isGroupOrderLocked;
  const shouldShowGroupOrderInviteAction = shouldShowGroupOrderHostActions && Boolean(groupOrderJoinCode); // prettier-ignore
  const shouldShowGroupOrderParticipantAction = shouldShowGroupOrder && !canSubmitCartOrder;
  const isBagInteractionDisabled = isPerformingBagAction || isGroupOrderCheckoutLocked;
  const areCartMutationInteractionsDisabled = isPerformingBagAction || isGroupOrderLocked;

  // ─── Handlers ──────────────────────────────────────────────────────────────────

  const handleGroupOrderLockModalClose = useCallback(() => {
    if (isLockingGroupOrder) return;

    cancelRequest();
  }, [cancelRequest, isLockingGroupOrder]);

  // ─── Tracking ─────────────────────────────────────────────────────────────

  const {
    trackContinuePressed,
    trackContinueDeliveryMinimumBlocked,
    trackViewMenuPressed,
    trackLineItemPressed,
  } = bagTracking;

  // ─── Navigation ───────────────────────────────────────────────────────────

  const router = useRouter();

  const handleViewMenu = useNavigateToLastInteractedStore({ isInsideModal: true }); // prettier-ignore
  const navigateToMenu = useNavigateToMenu();
  const navigateToBagMenu = useCallback(() => {
    trackViewMenuPressed();
    navigateToMenu({
      orderType: orderChannel,
      restaurantSlug,
      deliveryOrderDetail: { addressId },
      referrer: 'bag',
    });
  }, [orderChannel, restaurantSlug, addressId, navigateToMenu, trackViewMenuPressed]);

  // ─── Delivery Minimum ─────────────────────────────────────────────────────

  const isDeliveryMinimumBlockEnabled = useFeatureFlag('CELS-5341-order-delivery-minimum-block-enabled'); // prettier-ignore
  const deliveryMinimum = orderChannel === 'delivery' ? deliveryMinSubtotal : 0;
  const deliveryAmountNeeded = deliveryMinimum - (ledger?.subtotal ?? 0);
  const isBelowDeliveryMinimum = isDeliveryMinimumBlockEnabled && deliveryMinimum > 0 && deliveryAmountNeeded > 0; // prettier-ignore

  // ─── Auth State ───────────────────────────────────────────────────────────

  const handleContinue = useCallback(() => {
    trackContinuePressed();

    if (isBelowDeliveryMinimum) {
      const amountNeeded = formatNumber(deliveryAmountNeeded / 100, CURRENCY_FORMAT_OPTIONS);
      const minimumAmount = formatNumber(deliveryMinimum / 100, CURRENCY_FORMAT_OPTIONS);

      trackContinueDeliveryMinimumBlocked(minimumAmount, amountNeeded);

      addNoticeBanner({
        palette: 'neutral',
        text: formatMessage(messages.deliveryMinimumNotMet, {
          amount_needed: amountNeeded,
          minimum: minimumAmount,
        }),
      });

      return;
    }

    if (!isLoggedIn) {
      router.dismissTo({
        pathname: '/(auth)/join-or-sign-in',
        params: { redirect: '/bag' },
      });

      return;
    }

    // NOTE: Machine intercepts call to show upsell modal if eligible
    requestCheckoutLedger();
  }, [
    trackContinuePressed,
    trackContinueDeliveryMinimumBlocked,
    isBelowDeliveryMinimum,
    deliveryAmountNeeded,
    deliveryMinimum,
    addNoticeBanner,
    formatMessage,
    formatNumber,
    isLoggedIn,
    requestCheckoutLedger,
    router,
  ]);

  // ─── Contentful ───────────────────────────────────────────────────────────

  const {
    viewMenuCta,
    viewMenuA11y,
    continueToCheckoutCta,
    deliveryDisclosure,
    outpostDisclosure,
    timeslotUnavailableWarning,
    timeslotUnavailableWarningContinueCta,
    timeslotUnavailableWarningBackCta,
    loadingTitle,
    postRedirectTitle,
    postRedirectBody,
    postRedirectDelayedTitle,
    postRedirectDelayedBody,
    postRedirectDelayedCta,
  } = useBagContent();

  const disclosure = useBagDisclosure({
    orderChannel,
    showDeliveryFeeDisclosure,
    showOutpostPriceDifferenciationDisclosure,
    showDeliveryPriceDifferenciationDisclosure,
    deliveryFee,
    deliveryMinSubtotal,
    deliveryDisclosure,
    outpostDisclosure,
  });

  // ─── Upsells ──────────────────────────────────────────────────────────────

  const { categoryUpsells, isLoadingUpsells, shouldRenderCategoryUpsells, hideCategoryUpsells } =
    useUpsells({
      orderId,
      restaurantId,
      orderChannel,
      lineItems,
      isEnabled: canSubmitCartOrder && !isGroupOrderLocked,
    });

  const addUpsellToBag = useCallback(
    (payload: { quantity?: number; product: Partial<Product> }) => {
      const { quantity, product } = payload;

      addUpsell({
        productId: product.id ?? '',
        productName: product.name ?? '',
        quantity: quantity ?? 1,
        placementVariation,
      });
    },
    [addUpsell, placementVariation],
  );

  // ─── Upsell Exp. Logic ───────────────────────────────────────────────────────────────────

  const shouldRenderRail = upsellItems.length > 0;

  // ─── Loading ───────────────────────────────────────────────────────────────

  const isPostRedirectSubmission = isSubmittingOrder && !isShowingOrderPlayback;
  const isLoadingBag =
    isFetchingInitialData ||
    isPollingPayment ||
    isLoadingUpsells ||
    isPostOrder ||
    isPostRedirectSubmission;
  const isBagActionLoadingOverlayVisible =
    isPerformingBagAction && !isUnlockingGroupOrder && !isLoadingBag && !isSubmittingOrder;

  // ─── Floating Ctas ────────────────────────────────────────────────────────
  // When the keyboard is open on Android, the floating cta hides the input field.

  const { isKeyboardOpen } = useKeyboardListener();
  const shouldHideFloatingCtas = Platform.OS === 'android' && isKeyboardOpen;
  const shouldShowFloatingCtas = !shouldHideFloatingCtas && isBagValid && !isLoadingBag;
  const shouldShowWarning = isShowingWarning && (!isShowingRewardWarning || canApplyCartRewards);

  // ─── Loading State ────────────────────────────────────────────────────────

  const isPostRedirect = isPaymentPending || isPaymentDelayed || isPaymentFulfilled;
  const loadingTitlePostRedirect = isPaymentDelayed ? postRedirectDelayedTitle : postRedirectTitle; // prettier-ignore
  const loadingBodyPostRedirect = isPaymentDelayed ? postRedirectDelayedBody : postRedirectBody; // prettier-ignore
  const loadingStateTitle = isPostRedirect ? loadingTitlePostRedirect : loadingTitle; // prettier-ignore
  const loadingStateBody = isPostRedirect ? loadingBodyPostRedirect : ''; // prettier-ignore
  const loadingStateCta = isPostRedirect && isPaymentDelayed ? postRedirectDelayedCta : null; // prettier-ignore

  const handleStartPostRedirectDelay = useCallback(() => {
    if (!isPostRedirect) return;
    startPostRedirectDelay();
  }, [isPostRedirect, startPostRedirectDelay]);

  useFocusEffect(handleStartPostRedirectDelay);
  useAppState({ onVisible: handleStartPostRedirectDelay });

  // ─── Upsell Rendering ──────────────────────────────────────────────────────

  const canRenderUpsells =
    canSubmitCartOrder &&
    !isGroupOrderLocked &&
    !isUpsellScreenEnabled &&
    !isLoadingBag &&
    !isPostRedirect;
    !isLoadingPlacement;

  if (shouldRenderCategoryUpsells && canRenderUpsells && placementVariation === 'control') {
    return (
      <FocusScope enabled={Boolean(isFocused && isFocusable)}>
        <CategoryUpsells
          categories={categoryUpsells}
          upsellIdsBeingAdded={lineItemIdsBeingUpdated}
          addUpsellToBag={addUpsellToBag}
          onClose={closeBag}
          onContinue={hideCategoryUpsells}
        />
      </FocusScope>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <FocusScope enabled={Boolean(isFocused && isFocusable)}>
      {/* Loading state for the bag. */}
      {isLoadingBag ? (
        <Bag.LoadingState
          title={loadingStateTitle}
          body={loadingStateBody}
          cta={loadingStateCta}
          isLoading={isCancelingPayment}
          onPress={cancelPreviousIntent}
        />
      ) : null}

      {/* Empty state for the bag. */}
      {isBagEmpty && !isLoadingBag ? <Bag.EmptyState onViewMenu={handleViewMenu} /> : null}

      {/* Loading state overlay for when actions are performed in the bag, not needed for initial fetching or placing orders. */}
      <Bag.LoadingOpacity isLoading={isBagActionLoadingOverlayVisible} />

      <Bag.WarningModal visible={shouldShowWarning} onClose={cancelRequest}>
        {/* Warning that the selected date is in the future. */}
        {isShowingFutureDateWarning ? <Bag.FutureDateWarning onClose={cancelRequest} /> : null}

        {/* Warning that the reward was not applied. */}
        {isShowingRewardWarning && canApplyCartRewards ? (
          <BagRewardWarning
            message={rewardError}
            isRewardMissingSku={isRewardMissingSku}
            onViewMenu={handleViewMenu}
            onClose={cancelRequest}
          />
        ) : null}

        {/* Warning that the wanted time was adjusted. */}
        {isShowingTimeslotAdjustedNotice ? (
          <Bag.TimeslotUnavailableWarning
            wantedTime={wantedTime}
            previousWantedTime={previousWantedTime}
            message={timeslotUnavailableWarning}
            continueCta={timeslotUnavailableWarningContinueCta}
            backCta={timeslotUnavailableWarningBackCta}
            onContinue={requestCheckoutLedger}
            onBack={requestTimeChange}
          />
        ) : null}
      </Bag.WarningModal>

      {/* Actual bag contents. */}
      {isBagValid && !isLoadingBag ? (
        <Bag.ScrollView
          orderChannel={orderChannel}
          locationName={locationName}
          isGroupOrder={shouldShowGroupOrder}
          isGroupOrderParticipant={shouldShowGroupOrderParticipantAction}
          canUpdateOrderDetails={canUpdateCartOrderDetails}
          viewMenuCta={viewMenuCta}
          viewMenuA11y={viewMenuA11y}
          isDisabled={isBagInteractionDisabled}
          automaticallyAdjustKeyboardInsets={!isShowingModal}
          onViewMenu={navigateToBagMenu}
          requestChannelOrLocationChange={requestChannelOrLocationChange}
          requestDeliveryPreferencesChange={requestDeliveryPreferencesChange}
          onHeaderCloseBtnPress={closeBag}
        >
          <Bag.ContentContainer>
            {/* Header with channel, location, time and dropoff location. */}
            <Bag.HeaderContainer>
              <Bag.HeaderLine sizeMatch={[hasLongerLocationName ? '24' : '32']} numberOfLines={3}>
                <Bag.ChannelHeader
                  orderChannel={orderChannel}
                  isDisabled={!canUpdateCartOrderDetails}
                  requestChannelOrLocationChange={requestChannelOrLocationChange}
                />

                <TextSpace />

                <Bag.TimeConnector time={wantedTime} />

                <TextSpace />

                <Bag.TimeHeader
                  time={wantedTime}
                  isDisabled={!canUpdateCartOrderDetails}
                  requestTimeChange={requestTimeChange}
                />

                <TextSpace />

                {isFutureDate ? null : <Text>{'\n'}</Text>}

                {orderChannel === 'pickup' ? <Bag.LocationFromConnector /> : null}

                {orderChannel === 'delivery' ? <Bag.LocationToConnector /> : null}

                {orderChannel === 'outpost' ? <Bag.LocationAtConnector /> : null}

                <TextSpace />

                <Bag.LocationHeader
                  orderChannel={orderChannel}
                  locationName={locationName}
                  isDisabled={!canUpdateCartOrderDetails}
                  requestChannelOrLocationChange={requestChannelOrLocationChange}
                  requestDeliveryPreferencesChange={requestDeliveryPreferencesChange}
                />
              </Bag.HeaderLine>
            </Bag.HeaderContainer>

            {canUpdateCartOrderDetails &&
            orderChannel !== 'delivery' &&
            dropoffLocations.length >= 2 ? (
              <Bag.DropoffLocationPicker
                selectedDropoffLocationId={dropoffLocation}
                firstDropoffLocation={dropoffLocations[0]}
                secondDropoffLocation={dropoffLocations[1]}
                changeDropoffLocation={changeDropoffLocation}
              />
            ) : null}

            {canSubmitCartOrder && disclosure ? <Bag.Disclosure disclosure={disclosure} /> : null}

            {shouldShowGroupOrderConvertAction ? (
              <GroupOrderConvertAction
                isDisabled={isBagInteractionDisabled}
                onPress={requestGroupOrderConversion}
              />
            ) : null}

            {shouldShowGroupOrder ? (
              <View style={styles.groupOrderActionsContainer}>
                {isGroupOrderCheckoutLocked ? (
                  <BodyText sizeMatch={['16']}>{groupOrderingContent.lockedCartStatus}</BodyText>
                ) : null}

                {shouldShowGroupOrderInviteAction ? (
                  <GroupOrderBagButton.Share
                    isDisabled={isBagInteractionDisabled}
                    onPress={requestGroupOrderShare}
                  />
                ) : null}

                {isGroupOrderCheckoutLocked ? (
                  <GroupOrderBagButton.Unlock
                    isDisabled={isPerformingBagAction}
                    label={groupOrderingContent.unlockCartButtonLabel}
                    onPress={unlockGroupOrder}
                  />
                ) : null}

                {shouldShowGroupOrderHostActions ? (
                  <GroupOrderBagButton.Cancel
                    isDisabled={areCartMutationInteractionsDisabled}
                    onPress={requestGroupOrderLeave}
                  />
                ) : null}

                {shouldShowGroupOrderParticipantAction ? (
                  <GroupOrderBagButton.Leave
                    isDisabled={areCartMutationInteractionsDisabled}
                    onPress={requestGroupOrderLeave}
                  />
                ) : null}
              </View>
            ) : null}

            {/* List of line items, able to edit quantity or change name. */}
            <BagLineItems
              isGroupOrder={shouldShowGroupOrder}
              lineItems={lineItems}
              lineItemIdsBeingUpdated={lineItemIdsBeingUpdated}
              restaurantSlug={restaurantSlug}
              addressId={addressId}
              isDelivery={orderChannel === 'delivery'}
              isDisabled={areCartMutationInteractionsDisabled}
              trackLineItemPressed={trackLineItemPressed}
              startChangingLineItemQuantity={startChangingLineItemQuantity}
              stopChangingLineItemQuantity={stopChangingLineItemQuantity}
              changeLineItemQuantity={changeLineItemQuantity}
            />

            {canSubmitCartOrder &&
            !isGroupOrderLocked &&
            shouldRenderRail &&
            placementVariation === 'bag-rail-above' ? (
              <Bag.Upsell.Rail
                upsellProducts={upsellItems}
                upsellIdsBeingAdded={lineItemIdsBeingUpdated}
                addUpsellToBag={addUpsellToBag}
                isAddingUpsellToBag={isAddingUpsellToBag}
                startChangingLineItemQuantity={startChangingLineItemQuantity}
                stopChangingLineItemQuantity={stopChangingLineItemQuantity}
                isDisabled={isPerformingBagAction}
                placementVariation={placementVariation}
              />
            ) : null}

            {/* Rewards rail and add promo code field. */}
            {canApplyCartRewards ? <BagLoyalty orderState={orderState} /> : null}

            {canSubmitCartOrder && orderChannel === 'delivery' ? (
              <Bag.UtensilsCta
                shouldIncludeUtensils={shouldIncludeUtensils}
                isDisabled={areCartMutationInteractionsDisabled}
                onChange={changeUtensilsUsage}
              />
            ) : null}

            {canSubmitCartOrder ? (
              <Bag.Footer isDisabled={isPerformingBagAction}>
                {ledger?.subtotal ? <Bag.Subtotal subtotal={ledger.subtotal} /> : null}

                {ledger?.discounts[0] ? <Bag.Discount {...ledger.discounts[0]} /> : null}
              </Bag.Footer>
            ) : null}

            {shouldShowGroupOrderParticipantAction ? (
              <View style={styles.groupOrderParticipantContentSpacer} />
            ) : null}
          </Bag.ContentContainer>
        </Bag.ScrollView>
      ) : null}

      <GroupOrderConvertModal
        visible={isShowingGroupOrderConvertModal}
        isConvertingGroupOrder={isConvertingGroupOrder}
        onClose={cancelRequest}
        onSubmit={convertToGroupOrder}
      />

      {groupOrderJoinCode ? (
        <GroupOrderBagModal.Share visible={isShowingGroupOrderShareModal} onClose={cancelRequest}>
          <GroupOrderShareContent
            joinCode={groupOrderJoinCode}
            onClose={cancelRequest}
            onDone={cancelRequest}
          />
        </GroupOrderBagModal.Share>
      ) : null}

      {shouldShowGroupOrderHostActions ? (
        <GroupOrderBagModal.Cancel
          visible={isShowingGroupOrderLeaveModal}
          isCancelingGroupOrder={isLeavingGroupOrder}
          onClose={cancelRequest}
          onConfirm={leaveGroupOrder}
        />
      ) : null}

      {shouldShowGroupOrderParticipantAction ? (
        <GroupOrderBagModal.Leave
          visible={isShowingGroupOrderLeaveModal}
          isLeavingGroupOrder={isLeavingGroupOrder}
          onClose={cancelRequest}
          onConfirm={leaveGroupOrder}
        />
      ) : null}

      <Modal
        visible={isShowingGroupOrderLockModal}
        size="small"
        withSafeAreaView={false}
        accessibilityLabel={groupOrderingContent.lockCartModalTitle}
        onRequestClose={handleGroupOrderLockModalClose}
      >
        <GroupOrderConfirmationContent
          title={groupOrderingContent.lockCartModalTitle}
          body={groupOrderingContent.lockCartModalBody}
          dismissButtonLabel={groupOrderingContent.lockCartModalDismissButtonLabel}
          confirmButtonLabel={groupOrderingContent.lockCartModalConfirmButtonLabel}
          confirmButtonPalette="primary"
          isPerformingAction={isLockingGroupOrder}
          onClose={handleGroupOrderLockModalClose}
          onConfirm={lockGroupOrder}
        />
      </Modal>

      {shouldShowFloatingCtas ? (
        <Bag.FloatingCtas>
          {canSubmitCartOrder ? (
            <Bag.ContinueToCheckoutCta
              cta={
                isGroupOrderCheckoutLocked
                  ? groupOrderingContent.lockedCartCheckoutButtonLabel
                  : continueToCheckoutCta
              }
              isDisabled={!canContinueToCheckout}
              isLoading={isUnlockingGroupOrder}
              onPress={handleContinue}
            />
          ) : (
            <Bag.GroupOrderFooter isDisabled={isPerformingBagAction} onPress={navigateToBagMenu} />
          )}
        </Bag.FloatingCtas>
      ) : null}
    </FocusScope>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryMinimumNotMet: {
    defaultMessage:
      'Delivery minimum not met. Add {amount_needed} more to meet the {minimum} delivery minimum.',
    description: 'Bag | Continue to checkout | Delivery minimum not met',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  groupOrderActionsContainer: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing['3'],
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['2'],
  },
  groupOrderParticipantContentSpacer: {
    height: theme.spacing['40'],
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CURRENCY_FORMAT_OPTIONS: FormatNumberOptions = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagContentProps = {
  orderState: ReturnType<typeof useOrderState>;
};
